import * as appActions from '@/app/app-actions';
import { NAV_ITEMS, OTHER_NAV_ITEMS, STRORACLE_ENDPOINTS } from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as errorActions from '@/error-boundaries/error-actions';
import { DeckardCognitoUser } from '@/user/deckard-cognito-user';
import * as userSelectors from '@/user/user-selectors';
import { BaseSender } from '@/utils/base-sender';
import { loadReleaseNotesScript } from '@/utils/loadReleaseNotesScript';
import {
  getPlaceFromURLPath,
  getPlaceSpecificUrl,
} from '@/utils/place-detection';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import { strOracleDefaultApiSender } from '@/utils/stroracle-default-api-sender';
import Auth from '@aws-amplify/auth';
import * as Sentry from '@sentry/browser';
import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { CognitoLoginMgr } from '../login/cognito-login-manager/cognito-login-mgr';
import { apiClient } from '../utils/api-client';
import { toaster } from '../utils/toaster';
import * as userActions from './user-actions';
import { LOCALSTORAGE_MAP_INFOBOX_EXPAND } from './user-constants';
import { userSessionMgr } from './user-session-mgr';

function* userSignoutSaga() {
  CognitoLoginMgr.signOut();
  yield put(userActions.setIsAuthenticated({ cognitoUser: null }));
  // Clear authorized cities on logout
}

export function* userSignoutWatcher() {
  yield takeEvery(userActions.USER_SIGNOUT, userSignoutSaga);
}

interface OAuthConfig {
  domain: string;
  idpIdentifier: string;
  scope: string[];
  redirectSignIn: string;
  redirectSignOut: string;
  responseType: string;
}

interface SSOConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId: string;
  mandatorySignIn: boolean;
  oauth: OAuthConfig;
  deckardPlaces: string[];
  idpProviderName: string;
}

type DomainsResponse = {
  [domain: string]: SSOConfig;
};

/**
 * Will fetch emailDomains information from unauthenticated endpoint using non-city-based sender.
 * Determines which SSO email domains are valid for SSO sign in.
 */
function* getEmailDomains() {
  const response: AxiosResponse<DomainsResponse, unknown> = yield call(() =>
    apiClient.get<DomainsResponse>('/emailDomains')
  );
  return { emailDomains: response.data };
}

function* fetchCognitoClientIds() {
  const { emailDomains } = yield getEmailDomains();
  CognitoLoginMgr.setup(emailDomains);
}

interface Props {
  getSearch: () => string;
  showError: (main: string, details: string) => void;
}

export function* autoConfigureAmplify(props: Props) {
  const urlSearchParams = new URLSearchParams(props.getSearch());
  const params = Object.fromEntries(urlSearchParams.entries());

  const {
    state: ssoEmail, // "state" => email of logged in user
    error,
  } = params;

  if (error) {
    const isSSOLogin = Boolean(ssoEmail);
    const message = isSSOLogin
      ? 'Failed to login via SSO.'
      : 'Failed to login.';
    const details = urlSearchParams.get('error_description');

    yield Sentry.captureMessage(`autoConfigureAmplify Capture: ${message}`, {
      contexts: params,
    });
    yield props.showError(message, details);
  } else {
    const email = yield ssoEmail || CognitoLoginMgr.getLastAuthedEmail();
    yield CognitoLoginMgr.getInstance().amplifyConfigure(email);
    Sentry.configureScope((scope) => {
      scope.setUser({ username: email });
    });
    Sentry.captureMessage(`User login event by ${email}`, {
      contexts: { email, params },
    });
  }
}

class AutoLoginAttemptSagaError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AutoLoginAttemptSagaError';
  }
}

// Try to automatically log in on startup if previously logged in
function* autoLoginAttemptSaga() {
  let cognitoUser;

  try {
    cognitoUser = yield Auth.currentAuthenticatedUser();

    // assumes that if login fails, execution will go to catch block immediately
    yield put(userActions.onLoginSuccess({ cognitoUser }));
    Sentry.configureScope((scope) => {
      scope.setUser({ username: cognitoUser.username });
    });
    Sentry.captureMessage('User login event', { contexts: cognitoUser });
  } catch (err) {
    yield Sentry.captureException(new AutoLoginAttemptSagaError(err), {
      contexts: cognitoUser,
    });
  }
  // we already tried authenticating. Now proceed to either Login or landing page
  yield put(userActions.setIsPreAuthenticationPhase({ value: false }));
}

function* initiateCognitoAuthSetupSaga() {
  yield fetchCognitoClientIds();
  yield autoConfigureAmplify({
    getSearch: () => window.location.search,
    showError: (main, details) =>
      toaster.push(
        <div>
          {main}
          <div>{details}</div>
        </div>,
        20000
      ),
  });
  yield autoLoginAttemptSaga();
}

export function* initiateCognitoAuthSetupWatcher() {
  yield takeEvery(
    userActions.INITIATE_COGNITO_AUTH_SETUP,
    initiateCognitoAuthSetupSaga
  );
}

/**
 * Sets a new current city. Syncs BaseSender and store in terms of having the same current city
 * Also, fetches data for current city
 *
 * @param {*} action.payload.city the new currentCity
 * @param {*} action.payload.cityInfo if passed, will prevent fetching cityInfo from server,
 * when triggered by client during city switching there is nothing passed
 */
function* setCurrentCityEverywhereSaga({ payload: { city } }) {
  // ensure requested city is in user's roles
  const authorizedPlaces = yield select(userSelectors.authorizedPlacesList);
  const authorizedPlace = authorizedPlaces.find(
    (authCity) => authCity === city
  );

  const isAdmin = yield select(userSelectors.isAdmin);

  if (!isAdmin && !authorizedPlace === city) {
    // eslint-disable-next-line no-console
    console.error(
      'not admin, or city not authorized:',
      isAdmin,
      authorizedPlace,
      city
    );
    yield put(errorActions.setUnauthorizedErrorState());
  } else {
    BaseSender.setCurrentCity(city);
    localStorage.setItem(
      LOCALSTORAGE_MAP_INFOBOX_EXPAND,
      localStorage.getItem(LOCALSTORAGE_MAP_INFOBOX_EXPAND) || true
    );
    yield put(userActions.setCurrentCity({ city })); // sets current city in store
    yield put(appActions.getAllData());

    yield put(userActions.userSwitchedCity({ city }));
  }
}

function* userSwitchedCitySaga({ payload: { city } }) {
  if (window.location.href.includes(OTHER_NAV_ITEMS.PROFILE)) {
    yield put(push(getPlaceSpecificUrl(city, `/${NAV_ITEMS.home}`)));
  }
}

export function* userSwitchedCityWatcher() {
  yield takeLatest(userActions.USER_SWITCHED_CITY, userSwitchedCitySaga);
}

/**
 * Sets the last property details active tab from sessionStorage if any
 */
function* initPropertyDetailsTab() {
  const propertyDetailsTabFromSession = userSessionMgr.getPropertyDetailsTab();
  if (propertyDetailsTabFromSession) {
    yield put(
      dashboardActions.setActivePropertyDetailsTab({
        activeTab: propertyDetailsTabFromSession,
      })
    );
  }
}

export function* setCurrentCityEverywhereWatcher() {
  yield takeEvery(
    userActions.SET_CURRENT_CITY_EVERYWHERE,
    setCurrentCityEverywhereSaga
  );
}

function* getVersion(username) {
  const versionRequest = strOracleDefaultApiSender.getWithToken(
    STRORACLE_ENDPOINTS.VERSION,
    null,
    'json'
  );
  const { data: versionResponse } = yield versionRequest;

  // Accessing release_token from versionResponse
  userSessionMgr.setReleaseTokenSession(
    versionResponse.release_token,
    username
  );

  loadReleaseNotesScript(versionResponse.release_token);

  return { version: versionResponse.release_token };
}

/*
 * Will fetch defaultPlace from the /defaultPlace authenticated endpoint using
 * non-city-based sender and store it in session storage.
 * Determines which city group is assigned to a user.
 */
function* getDefaultPlace(username) {
  const currentCity = userSessionMgr.getDefaultCitySession(username);
  if (currentCity && !Object.keys(NAV_ITEMS).includes(currentCity)) {
    return { defaultPlace: currentCity };
  }
  const defaultCityRequest = strOracleDefaultApiSender.getWithToken(
    STRORACLE_ENDPOINTS.DEFAULT_PLACE
  );
  const { data: defaultCityResponse } = yield defaultCityRequest;
  userSessionMgr.setDefaultCitySession(defaultCityResponse.place, username);
  return { defaultPlace: defaultCityResponse.place };
}

// function* getIsAdmin() {
//   const isAdminRequest = strOracleDefaultApiSender.getWithToken('/isAdmin');
//   const { data: isAdmin } = yield isAdminRequest;
//   return isAdmin.isAdmin;
// }

// Retrieve current groups and set them as authorized cities
function* onLoginSuccessSaga({ payload: { cognitoUser } }) {
  // first we give access to the user
  yield put(userActions.setIsAuthenticated({ cognitoUser }));

  try {
    const deckardCognitoUser = new DeckardCognitoUser(cognitoUser);
    const { isAdminGroup, username, firstName, lastName, defaultCity } =
      deckardCognitoUser;

    const cityFromURLPath = getPlaceFromURLPath(window.location.pathname);
    let currentCity = cityFromURLPath || deckardCognitoUser.defaultCity;
    if (!currentCity) {
      const { defaultPlace } = yield getDefaultPlace(username);
      currentCity = defaultPlace;
    }

    const isAdmin = /* yield getIsAdmin() */ isAdminGroup;

    /*
     * This is mandatory. Will store in localStorage the last authenticated email.
     * The last authenticated email ls value is used for determining which awsConfig
     * to use for configuring Amplify
     */
    CognitoLoginMgr.getInstance().registerLoginSuccess(username);

    yield setCurrentCityEverywhereSaga(
      userActions.setCurrentCityEverywhere({ city: currentCity })
    );

    const { data: authorizedCities } =
      yield strOracleApiSender.rawGet('user_cities');
    const attributes = { username, isAdmin, defaultCity };

    // set user info
    yield put(userActions.setUserFirstName({ firstName }));
    yield put(userActions.setUserLastName({ lastName }));
    yield put(userActions.storeUserAttributes({ attributes }));
    yield put(userActions.setAuthorizedCities({ authorizedCities }));

    // set some initial states
    yield initPropertyDetailsTab();

    // Least priority, fetching version for release notes
    yield getVersion(username);

    // fetching user roles failed, set user to unauthorized
  } catch (err) {
    console.error('Retrieving roles or city failed: ', err); // eslint-disable-line no-console
    // if retrieving roles and or default city fails, set unauthorized state
    yield put(errorActions.setUnauthorizedErrorState());
  }
}

export function* onLoginSuccessWatcher() {
  yield takeEvery(userActions.ON_LOGIN_SUCCESS, onLoginSuccessSaga);
}
